import { c as _c } from "react/compiler-runtime";
import type { DataIdentifier, Entity } from '@onefootprint/request-types/dashboard';
import { useObjectUrl } from '@onefootprint/ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useEntityVault from '@/entities/hooks/use-entity-vault';
import useDecryptControls from '@/entity/hooks/use-decrypt-controls';
import useVaultDocument, { parseDocumentIdentifier } from '../../../../hooks/use-vault-document';
import type { ComparisonImageSource } from './types';
const useVaultImage = (entity, documentIdentifier) => {
  const $ = _c(35);
  const {
    t
  } = useTranslation("entity-details");
  const document = useVaultDocument(entity.id, documentIdentifier, true);
  let t0;
  if ($[0] !== document.version || $[1] !== entity.data || $[2] !== entity.id) {
    t0 = {
      entityAttributes: entity.data,
      entityId: entity.id,
      seqno: document.version
    };
    $[0] = document.version;
    $[1] = entity.data;
    $[2] = entity.id;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const vault = useEntityVault(t0);
  const decryptControls = useDecryptControls();
  const field = documentIdentifier as DataIdentifier;
  const identifier = parseDocumentIdentifier(documentIdentifier)?.identifier;
  const attribute = entity.data.find(candidate_0 => candidate_0.identifier === field) ?? entity.data.find(candidate => candidate.identifier === identifier);
  const canDecrypt = attribute?.isDecryptable ?? document.match?.document.canDecrypt;
  const canView = !!document.match && canDecrypt && !document.match.document.redactedAt && !document.isLoading && !document.isUnavailable && !document.error && !vault.error;
  const value = canView ? vault.data?.vault[field] : undefined;
  let bytes;
  let t1;
  if ($[4] !== value) {
    bytes = typeof value === "string" && value.length > 0 ? value.replace(/\s/g, "") : null;
    t1 = bytes !== null && /^[A-Za-z0-9+/]*={0,2}$/.test(bytes) && (bytes.includes("=") ? bytes.length % 4 === 0 : bytes.length % 4 !== 1);
    $[4] = value;
    $[5] = bytes;
    $[6] = t1;
  } else {
    bytes = $[5];
    t1 = $[6];
  }
  const validBase64 = t1;
  let t2;
  if ($[7] !== bytes || $[8] !== validBase64) {
    t2 = {
      bytes,
      validBase64
    };
    $[7] = bytes;
    $[8] = validBase64;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const {
    bytes: bytes_0,
    validBase64: validBase64_0
  } = t2;
  const {
    objectUrl,
    mimeType
  } = useObjectUrl(validBase64_0 ? bytes_0 : null);
  let t3;
  if ($[10] !== decryptControls || $[11] !== entity || $[12] !== field || $[13] !== t || $[14] !== vault) {
    t3 = async () => {
      const response = await decryptControls.decrypt({
        entity,
        fields: [field],
        reason: t(_temp)
      });
      if (response) {
        vault.update({
          vault: response,
          transforms: {},
          dataKinds: {}
        });
      }
    };
    $[10] = decryptControls;
    $[11] = entity;
    $[12] = field;
    $[13] = t;
    $[14] = vault;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const decrypt = t3;
  if (document.isLoading || vault.isPending) {
    let t4;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        status: "loading"
      };
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    return t4;
  }
  if (document.error || vault.error) {
    let t4;
    if ($[17] !== document) {
      t4 = {
        status: "error",
        onRetry: () => void document.retry()
      };
      $[17] = document;
      $[18] = t4;
    } else {
      t4 = $[18];
    }
    return t4;
  }
  if (document.isUnavailable || !document.match) {
    let t4;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        status: "unavailable"
      };
      $[19] = t4;
    } else {
      t4 = $[19];
    }
    return t4;
  }
  if (document.match.document.redactedAt) {
    let t4;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        status: "redacted"
      };
      $[20] = t4;
    } else {
      t4 = $[20];
    }
    return t4;
  }
  if (!canDecrypt) {
    let t4;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        status: "denied"
      };
      $[21] = t4;
    } else {
      t4 = $[21];
    }
    return t4;
  }
  if (!bytes_0) {
    let t4;
    if ($[22] !== decrypt) {
      t4 = () => void decrypt();
      $[22] = decrypt;
      $[23] = t4;
    } else {
      t4 = $[23];
    }
    let t5;
    if ($[24] !== decryptControls || $[25] !== field) {
      t5 = decryptControls.isDecrypting([field]);
      $[24] = decryptControls;
      $[25] = field;
      $[26] = t5;
    } else {
      t5 = $[26];
    }
    let t6;
    if ($[27] !== t4 || $[28] !== t5) {
      t6 = {
        status: "locked",
        onDecrypt: t4,
        isDecrypting: t5
      };
      $[27] = t4;
      $[28] = t5;
      $[29] = t6;
    } else {
      t6 = $[29];
    }
    return t6;
  }
  if (!validBase64_0) {
    let t4;
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        status: "unsupported"
      };
      $[30] = t4;
    } else {
      t4 = $[30];
    }
    return t4;
  }
  if (!objectUrl) {
    let t4;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        status: "loading"
      };
      $[31] = t4;
    } else {
      t4 = $[31];
    }
    return t4;
  }
  if (!mimeType?.startsWith("image/")) {
    let t4;
    if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        status: "unsupported"
      };
      $[32] = t4;
    } else {
      t4 = $[32];
    }
    return t4;
  }
  let t4;
  if ($[33] !== objectUrl) {
    t4 = {
      status: "ready",
      src: objectUrl
    };
    $[33] = objectUrl;
    $[34] = t4;
  } else {
    t4 = $[34];
  }
  return t4;
};
export default useVaultImage;
function _temp($) {
  return $.decrypt.documentDetailsReason;
}